import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError';
}

export default function LoadingPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { pollScan } = useAuth();

  const steps = t('loading.steps', { returnObjects: true }) as string[];
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [visibleSteps, setVisibleSteps] = useState<number[]>([0]);

  useEffect(() => {
    if (!id) return;

    const pollController = new AbortController();
    let isActive = true;
    let redirectTimeout: ReturnType<typeof setTimeout> | undefined;

    const stepInterval = setInterval(() => {
      setCurrentStep(prev => {
        const next = (prev + 1) % steps.length;
        setVisibleSteps(existing => [...existing, next].slice(-4));
        return next;
      });
      setProgress(prev => Math.min(prev + 100 / steps.length, 95));
    }, 2000);

    pollScan(id, pollController.signal)
      .then(scan => {
        if (!isActive) return;

        clearInterval(stepInterval);
        setProgress(100);
        redirectTimeout = setTimeout(() => {
          if (isActive) {
            navigate(
              scan.projectId
                ? `/projects/${scan.projectId}`
                : `/scan/${scan.id}/results`,
              { replace: true }
            );
          }
        }, 800);
      })
      .catch(error => {
        if (!isActive || isAbortError(error)) return;

        clearInterval(stepInterval);
        console.error('Scan polling failed:', error);
        navigate('/scan', { replace: true });
      });

    return () => {
      isActive = false;
      pollController.abort();
      clearInterval(stepInterval);
      if (redirectTimeout !== undefined) {
        clearTimeout(redirectTimeout);
      }
    };
  }, [id, navigate, pollScan, steps.length]);

  return (
    <div className="loading-page">
      <div className="animate-fade-in">
        <div style={{ textAlign: 'center', marginBottom: 48, paddingTop: 16 }}>
          <div
            style={{
              width: 88,
              height: 88,
              margin: '0 auto 28px',
              position: 'relative',
            }}
          >
            <svg
              viewBox="0 0 88 88"
              fill="none"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
              }}
            >
              <circle
                cx="44"
                cy="44"
                r="40"
                stroke="var(--border)"
                strokeWidth="2"
              />
              <circle
                cx="44"
                cy="44"
                r="40"
                stroke="var(--accent-primary)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="60 190"
                style={{
                  animation: 'spin 1.2s linear infinite',
                  transformOrigin: '44px 44px',
                }}
              />
              <circle
                cx="44"
                cy="44"
                r="22"
                fill="var(--accent-dim)"
                style={{ animation: 'pulse 2s ease-in-out infinite' }}
              />
              <text
                x="44"
                y="50"
                textAnchor="middle"
                fill="var(--accent-primary)"
                fontSize="18"
                fontFamily="var(--font-mono)"
                fontWeight="600"
              >
                AI
              </text>
            </svg>
          </div>

          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 32,
              fontWeight: 800,
              color: 'var(--text-primary)',
              marginBottom: 10,
              letterSpacing: '-0.02em',
            }}
          >
            {t('loading.title')}
          </h1>
          <p style={{ fontSize: 16, color: 'var(--text-secondary)' }}>
            {t('loading.subtitle')}
          </p>
        </div>

        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              height: 3,
              background: 'var(--bg-tertiary)',
              borderRadius: 100,
              overflow: 'hidden',
              marginBottom: 10,
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                background:
                  'linear-gradient(90deg, var(--accent-primary), var(--success))',
                borderRadius: 100,
                transition: 'width 1.8s ease',
                boxShadow: '0 0 8px rgba(0, 200, 255, 0.5)',
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              fontSize: 12,
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            <span>0%</span>
            <span style={{ color: 'var(--accent-primary)' }}>
              {Math.round(progress)}%
            </span>
            <span>100%</span>
          </div>
        </div>

        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '10px 16px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--bg-tertiary)',
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: 'var(--danger)',
              }}
            />
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: 'var(--warning)',
              }}
            />
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: 'var(--success)',
              }}
            />
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
                color: 'var(--text-muted)',
                letterSpacing: '0.05em',
                wordBreak: 'break-word',
              }}
            >
              compliance-scanner - directive 2006/42/ec
            </span>
          </div>

          <div className="loading-log-body">
            {steps.map((step, index) => {
              const isVisible = visibleSteps.includes(index);
              const isCurrent = index === currentStep;
              const isPast =
                visibleSteps.indexOf(index) < visibleSteps.length - 1;

              if (!isVisible) return null;

              return (
                <div
                  key={index}
                  className="loading-step"
                  style={{
                    animation: 'fadeIn 0.3s ease',
                    opacity: isPast ? 0.5 : 1,
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  >
                    {new Date().toLocaleTimeString('en', {
                      hour12: false,
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </span>

                  {isCurrent ? (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'var(--accent-primary)',
                        flexShrink: 0,
                        marginTop: 5,
                        boxShadow: '0 0 6px var(--accent-primary)',
                      }}
                      className="animate-pulse"
                    />
                  ) : (
                    <span
                      style={{
                        color: 'var(--success)',
                        fontSize: 12,
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                    >
                      ✓
                    </span>
                  )}

                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 13,
                      color: isCurrent
                        ? 'var(--text-primary)'
                        : 'var(--text-muted)',
                      lineHeight: 1.5,
                      wordBreak: 'break-word',
                    }}
                  >
                    {step}
                  </span>
                </div>
              );
            })}

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 4,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13,
                  color: 'var(--text-muted)',
                }}
              >
                {new Date().toLocaleTimeString('en', {
                  hour12: false,
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </span>
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 16,
                  background: 'var(--accent-primary)',
                  animation: 'pulse 1s step-end infinite',
                }}
              />
            </div>
          </div>
        </div>

        <div className="loading-standards">
          {[
            'Annex I',
            'EN ISO 12100',
            'EN ISO 14120',
            'EN ISO 13857',
            'EN ISO 13849',
            'EN 62061',
            'EN ISO 13850',
            'Annex VII',
          ].map(standard => (
            <span
              key={standard}
              style={{
                padding: '4px 10px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: 100,
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
                color: 'var(--text-muted)',
                letterSpacing: '0.04em',
              }}
            >
              {standard}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
